// Панель диалога с ассистентом.
//
// На широком экране — карточка у кнопки; на телефоне — весь экран, как лист
// «Ещё»: на 360 точках карточка в углу нечитаема.
//
// ОТВЕТ СИНХРОННЫЙ И НЕ МГНОВЕННЫЙ (полторы секунды на обращение к модели плюс
// запросы к базе). Поэтому вопрос попадает в ленту сразу, а под ним — строка
// «думает». Без неё человек жмёт «Отправить» второй раз, и школа платит дважды.
import { Loader2, Send, SquarePen } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useEscapeToClose } from '@/lib/overlay-stack';
import { cn } from '@/lib/utils';

import type { AssistantEntity, AssistantMessage } from '../api/assistant.api';
import {
  useAskAssistant,
  useAssistantMessages,
  useAssistantThreads,
  useCreateThread,
  useQuestionKey,
  useScreenContext,
} from '../hooks/useAssistant';

/**
 * Разметка ответа: жирный текст и номера сущностей.
 *
 * Модель отвечает markdown'ом — это её обычная манера, и запретить её в
 * инструкции надёжно нельзя: получится «не пиши звёздочки», а звёздочки всё
 * равно появятся. Поэтому разбираем здесь, но ровно два вида разметки:
 * `**жирный**` и номера. Полноценный markdown-рендер сюда тащить незачем —
 * ответы короткие, а лишняя библиотека в бандле дороже пользы.
 *
 * Номера (`#1234`) становятся ссылками с именем. Имена приходят ОТДЕЛЬНОЙ
 * картой от сервера и только для тех номеров, которые назвала модель: сама
 * модель имён не получала. Номер без карты остаётся текстом — значит, карточка
 * вне прав сотрудника либо объединена.
 */
function renderAnswer(
  text: string,
  entities: Record<string, AssistantEntity> | undefined,
): (string | JSX.Element)[] {
  const out: (string | JSX.Element)[] = [];
  let key = 0;

  // Сначала жирный: он может обрамлять номер («**#1234**»), обратный порядок
  // разрезал бы ссылку пополам.
  for (const chunk of text.split(/(\*\*[^*]+\*\*)/g)) {
    if (!chunk) continue;
    const bold = /^\*\*([^*]+)\*\*$/.exec(chunk);
    const inner = bold ? bold[1]! : chunk;
    const parts = withEntities(inner, entities, () => (key += 1));
    out.push(
      bold ? (
        <strong key={`b${(key += 1)}`} className="font-medium">
          {parts}
        </strong>
      ) : (
        <span key={`t${(key += 1)}`}>{parts}</span>
      ),
    );
  }

  return out;
}

function withEntities(
  text: string,
  entities: Record<string, AssistantEntity> | undefined,
  nextKey: () => number,
): (string | JSX.Element)[] {
  if (!entities || Object.keys(entities).length === 0) return [text];

  const parts: (string | JSX.Element)[] = [];
  const pattern = /#\d{1,10}/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const entity = entities[match[0]];
    if (!entity) continue;
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <Link
        key={`e${nextKey()}`}
        to={entity.href}
        className="rounded bg-primary/10 px-1 text-primary hover:underline"
      >
        {entity.label}
      </Link>,
    );
    last = match.index + match[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Bubble({ message }: { message: AssistantMessage }): JSX.Element {
  const mine = message.role === 'user';
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap rounded-card px-3 py-2 text-sm',
          mine ? 'bg-primary text-canvas-white' : 'bg-whisper-white text-ink-black',
          message.error && 'border border-soft-orange',
        )}
      >
        {mine ? message.text : renderAnswer(message.text, message.entities)}
      </div>
    </div>
  );
}

export function AssistantPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [withScreen, setWithScreen] = useState(true);

  const threads = useAssistantThreads(true);
  const messages = useAssistantMessages(threadId);
  const createThread = useCreateThread();
  const ask = useAskAssistant();
  const screen = useScreenContext();
  const questionKey = useQuestionKey(draft);

  useEscapeToClose(onClose, true, panelRef);

  // Открываем последний диалог: чаще всего человек возвращается к начатому.
  useEffect(() => {
    if (threadId || !threads.data) return;
    const latest = threads.data[0];
    if (latest) setThreadId(latest.id);
    else if (!createThread.isPending)
      createThread.mutate(undefined, { onSuccess: (t) => setThreadId(t.id) });
  }, [threadId, threads.data, createThread]);

  // Прокрутка присваиванием, а не `scrollTo`: последнего нет в jsdom, и тест
  // на панель падал бы на пустом месте.
  useEffect(() => {
    const feed = feedRef.current;
    if (feed) feed.scrollTop = feed.scrollHeight;
  }, [messages.data?.length, ask.isPending]);

  const canSend = useMemo(
    () => draft.trim().length > 0 && Boolean(threadId) && !ask.isPending,
    [draft, threadId, ask.isPending],
  );

  function send(): void {
    if (!canSend || !threadId) return;
    ask.mutate(
      {
        threadId,
        text: draft.trim(),
        ...(withScreen && screen ? { screen } : {}),
        idempotencyKey: questionKey,
      },
      { onSuccess: () => setDraft('') },
    );
  }

  const list = messages.data ?? [];

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Ассистент CRM"
      className={cn(
        'fixed z-[55] flex flex-col overflow-hidden bg-canvas-white',
        // Телефон — весь экран; широкий экран — карточка над кнопкой.
        'inset-0 lg:inset-auto lg:bottom-20 lg:right-4 lg:h-[620px] lg:max-h-[calc(100dvh-7rem)] lg:w-[400px]',
        'lg:rounded-card lg:border lg:border-divider-gray lg:shadow-xl',
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-divider-gray px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-black">Спросить CRM</p>
          <p className="truncate text-xs text-muted-foreground">
            Отвечает по данным школы — проверяйте важное
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => createThread.mutate(undefined, { onSuccess: (t) => setThreadId(t.id) })}
            aria-label="Новый диалог"
            title="Новый диалог"
            className="rounded-base p-2 text-muted-foreground hover:bg-whisper-white"
          >
            <SquarePen className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Свернуть"
            className="rounded-base px-2 py-1 text-sm text-muted-foreground hover:bg-whisper-white lg:hidden"
          >
            Свернуть
          </button>
        </div>
      </header>

      <div ref={feedRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {list.length === 0 && !ask.isPending ? (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Спросите про данные школы или про то, как работать в CRM. Например:</p>
            <ul className="list-inside list-disc space-y-1">
              <li>что у меня сегодня по расписанию</li>
              <li>как открыть смену</li>
              <li>чем «Удалить» отличается от «Возврата»</li>
            </ul>
            <p className="pt-2">Ассистент только читает: ничего не меняет и клиентам не пишет.</p>
          </div>
        ) : null}

        {list.map((m) => (
          <Bubble key={m.id} message={m} />
        ))}

        {ask.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Думает…
          </div>
        ) : null}

        {ask.isError ? (
          <div className="rounded-base bg-soft-orange/10 px-3 py-2 text-xs text-ink-black">
            Ответ не пришёл. Нажмите «Отправить» ещё раз — повтор с тем же вопросом не оплачивается
            дважды.
          </div>
        ) : null}
      </div>

      <div className="border-t border-divider-gray px-4 py-3">
        {screen ? (
          <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={withScreen}
              onChange={(e) => setWithScreen(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Учитывать открытый экран: {screen}
          </label>
        ) : null}

        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Ваш вопрос… (Enter — отправить, Shift+Enter — перенос)"
            aria-label="Вопрос ассистенту"
            className="max-h-32 min-h-[42px] flex-1 resize-none rounded-base border border-divider-gray bg-canvas-white px-3 py-2 text-sm text-ink-black outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            aria-label="Отправить"
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-base bg-primary text-canvas-white disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
